import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { Calendar, ChevronDown } from "lucide-react";
import DatePicker from "./DatePicker";
import { ThemeContext } from "../constant/theme";
import { useContext } from "react";

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * DateRangePicker — composes two DatePicker instances (from/to) with quick
 * presets. Value shape: { from: "YYYY-MM-DD"|null, to: "YYYY-MM-DD"|null }.
 *
 * The popup is rendered via a React Portal (document.body), positioned from
 * the trigger button's own bounding rect — the same pattern DatePicker.jsx
 * (which it composes) and every other dropdown in the app already use.
 * Without that, this panel used to render `position: absolute` inside
 * whatever grid cell the Filters panel put it in, which could get clipped by
 * an ancestor's overflow/stacking context depending on layout — the popup
 * (and the "From"/"To" pickers inside it) could end up invisible or
 * unreachable instead of appearing anchored under the button.
 *
 * Props: theme, isRTL, value, onChange, presetsLabels ({today, thisWeek,
 * thisMonth, thisQuarter, custom}), style
 */
const DateRangePicker = ({ value = {}, onChange, isRTL = false, presetsLabels = {}, style: outerStyle }) => {
  const { theme } = useContext(ThemeContext);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
      width: Math.max(rect.width, 320),
    });
  }, []);

  useEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onOutsideClick = (e) => {
      // The From/To DatePickers nested inside this panel render their own
      // calendar via a SEPARATE portal straight to document.body — it's not
      // a DOM descendant of panelRef even though it's visually part of this
      // popup, so a plain "is the click inside panelRef" check treats every
      // day click as an outside click and closes this panel out from under
      // the calendar the user is still using. The data attribute marks that
      // nested portal so it counts as "inside" too.
      if (e.target.closest && e.target.closest('[data-datepicker-portal]')) return;
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onScroll = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      updatePosition();
    };
    const onResize = () => updatePosition();
    document.addEventListener("mousedown", onOutsideClick);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onOutsideClick);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, updatePosition]);

  const applyPreset = (preset) => {
    const now = new Date();
    let from, to = new Date(now);
    if (preset === "today") { from = new Date(now); }
    else if (preset === "thisWeek") { from = new Date(now); from.setDate(now.getDate() - now.getDay()); }
    else if (preset === "thisMonth") { from = new Date(now.getFullYear(), now.getMonth(), 1); }
    onChange?.({ from: toISO(from), to: toISO(to) });
    setOpen(false);
  };

  const placeholderText = isRTL ? "اختر النطاق الزمني" : "Select date range";
  const label = value.from && value.to
    ? `${value.from} → ${value.to}`
    : (presetsLabels.placeholder || placeholderText);

  const presets = [
    ["today", presetsLabels.today || (isRTL ? "اليوم" : "Today")],
    ["thisWeek", presetsLabels.thisWeek || (isRTL ? "هذا الأسبوع" : "This Week")],
    ["thisMonth", presetsLabels.thisMonth || (isRTL ? "هذا الشهر" : "This Month")],
  ];

  const isDark = theme === "dark";

  const panel = open
    ? ReactDOM.createPortal(
        <div
          ref={panelRef}
          style={{
            position: "absolute", top: pos.top, left: pos.left, width: pos.width,
            zIndex: 99999, padding: 16, borderRadius: 16,
            background: isDark ? "rgba(15,23,42,0.98)" : "#ffffff",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"}`,
            boxShadow: isDark ? "0 20px 60px rgba(0,0,0,0.5)" : "0 20px 60px rgba(0,0,0,0.15)",
            display: "flex", flexDirection: "column", gap: 12,
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {presets.map(([key, presetLabel]) => (
              <button
                key={key}
                type="button"
                onClick={() => applyPreset(key)}
                style={{
                  padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                  border: `1px solid ${isDark ? "rgba(59,130,246,0.4)" : "rgba(37,99,235,0.3)"}`,
                  background: isDark ? "rgba(59,130,246,0.12)" : "rgba(37,99,235,0.08)",
                  color: isDark ? "#93c5fd" : "#1d4ed8",
                  cursor: "pointer",
                }}
              >
                {presetLabel}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <DatePicker
              theme={theme}
              isRTL={isRTL}
              label={presetsLabels.from || (isRTL ? "من" : "From")}
              value={value.from}
              onChange={(d) => onChange?.({ ...value, from: d })}
            />
            <DatePicker
              theme={theme}
              isRTL={isRTL}
              label={presetsLabels.to || (isRTL ? "إلى" : "To")}
              value={value.to}
              onChange={(d) => onChange?.({ ...value, to: d })}
            />
          </div>
          <div style={{ display: "flex", justifyContent: isRTL ? "flex-start" : "flex-end" }}>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                padding: "8px 18px", borderRadius: 10, border: "none",
                background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff",
                fontSize: 12.5, fontWeight: 700, cursor: "pointer",
              }}
            >
              {presetsLabels.done || (isRTL ? "تم" : "Done")}
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div style={{ position: "relative", ...outerStyle }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          padding: "10px 14px", borderRadius: 12, boxSizing: "border-box",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"}`,
          background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.9)",
          color: isDark ? "#f1f5f9" : "#1e293b",
          fontSize: 13, fontWeight: 500, cursor: "pointer",
        }}
      >
        <Calendar size={15} style={{ opacity: 0.7, flexShrink: 0 }} />
        <span style={{ flex: 1, textAlign: isRTL ? "right" : "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <ChevronDown size={14} style={{ opacity: 0.6, flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
      </button>

      {panel}
    </div>
  );
};

export default DateRangePicker;
